import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import {
  fetchWellSummaryDashboard,
  fetchStatusCounts,
  selectWellSummaryLoading,
  selectWellSummaryError,
  selectWellMopTypes,
  selectWellDashboardRaw,
  selectStatusLoading,
  selectStatusValues,
  selectFetchDataSummary,
  selectApplicationsIdMap,
  fetchApplicationsIdMap,
} from './WellSummaryDashboardSlice';
import { isMopClickable } from './clickableMopsConfig';
import NavigationIcons from '../navigation/components/NavigationIcons';
import EmptyState from '../../components/empty-state/EmptyState';

export const WellStatusGrid: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const loading = useAppSelector(selectWellSummaryLoading);
  const error = useAppSelector(selectWellSummaryError);
  const mopTypes = useAppSelector(selectWellMopTypes) || {};
  const raw = useAppSelector(selectWellDashboardRaw);
  const fetchDataSummary = useAppSelector(selectFetchDataSummary);
  const applicationsIdMap = useAppSelector(selectApplicationsIdMap);

  const [hasScrollbar, setHasScrollbar] = useState(false);
  const bodyContainerRef = React.useRef<HTMLDivElement>(null);

  // Handler for cell clicks - navigate to RealtimeInsights page
  const handleCellClick = (mop: string, valueFromStatus: number, status: string) => {
    navigate('/realtime-insights', {
      state: {
        mop,
        valueFromStatus,
        status,
      },
    });
  };

  // new: bring selected filters from WellFilterSlice
  const selectedFilters = useAppSelector((state) => state.WellFiltersSlice.selectedFilters);

  // Initial fetch if nothing is in state
  useEffect(() => {
    if (!raw && !loading) {
      dispatch(fetchWellSummaryDashboard());
    }
  }, [dispatch, raw, loading]);

  // Define status columns once
  const statusColumns = [
    {
      key: 'active',
      statusKey: 'Active',
      label: 'Enabled',
      bgColor: '#054F31',
      borderColor: '#05603A',
      fontColor: '#6CE9A6',
    },
    {
      key: 'running',
      statusKey: 'Running',
      label: 'Running',
      bgColor: '#054F31',
      borderColor: '#05603A',
      fontColor: '#6CE9A6',
    },
    {
      key: 'shutdown',
      statusKey: 'Shutdown',
      label: 'Shutdown',
      bgColor: '#7A271A',
      borderColor: '#991A0F',
      fontColor: '#FDA29B',
    },
    {
      key: 'commFail',
      statusKey: 'CommFail',
      label: 'Comm fail',
      bgColor: '#13171F',
      borderColor: '#373A41',
      fontColor: '##CECFD2',
    },
    {
      key: 'restarted',
      statusKey: 'Restarted',
      label: 'Restarted',
      bgColor: '#7A2E0E',
      borderColor: '#93370D',
      fontColor: '#FEC84B',
    },
    {
      key: 'cycles',
      statusKey: 'Cycles',
      label: 'Cycling',
      bgColor: '#7A2E0E',
      borderColor: '#93370D',
      fontColor: '#FEC84B',
    },
    {
      key: 'offline',
      statusKey: 'Offline',
      label: 'Disabled',
      bgColor: '#253040',
      borderColor: '#4A5463',
      fontColor: '#CDD5DF',
    },
    {
      key: 'noData',
      statusKey: 'NoData',
      label: 'No data',
      bgColor: '#122960',
      borderColor: '#1D4B84',
      fontColor: '#C8DBFF',
    },
  ];

  // build mopTypeRows but filter by selected MOPs if present (supports multiple + aliases)
  // ensures selected MOP rows exist even if server didn't return them (zeroed placeholders)
  const mopTypeRows = useMemo(() => {
    // existing rows from server
    const serverRows = Object.entries(mopTypes || {}).map(([key, data]) => ({
      key,
      label: data.displayName || '',
      data,
    }));

    // ======= Alias mapping (same as before) =======
    const mopAliasMap: Record<string, string> = {
      'gas lift': 'Gas Lift',
      gaslift: 'Gas Lift',
      gl: 'Gas Lift',
      gla: 'Gas Lift',
      'gap l': 'Gas Lift',
      gapl: 'Gas Lift',
      gal: 'Gas Lift',

      'rod lift': 'Rod Lift',
      rodlift: 'Rod Lift',
      rod: 'Rod Lift',
      'rod-lift': 'Rod Lift',
      rod_lift: 'Rod Lift',

      'chemical injection': 'Chemical Injection',
      chemicalinjection: 'Chemical Injection',
      'chemical-injection': 'Chemical Injection',

      esp: 'ESP',
      'electrical submersible pump': 'ESP',

      pcp: 'PCP',
      srp: 'SRP',
      facility: 'Facility',
    };

    const normalize = (s: string) =>
      (s || '').toString().trim().toLowerCase().replace(/[-_]/g, ' ').replace(/[.,]/g, '').replace(/\s+/g, ' ');

    const mapToCanonical = (raw: string) => {
      const n = normalize(raw);
      if (mopAliasMap[n]) return mopAliasMap[n];
      return raw;
    };

    // get selected filters for "Method of production"
    const rawSelected = (selectedFilters || [])
      .filter((f) => ['Method of production', 'Method of Production', 'MOP', 'Method'].includes(f.section))
      .map((f) => (f.value || '').toString())
      .filter(Boolean);

    // If no MOP filters selected -> return all server rows (original behavior)
    if (rawSelected.length === 0) {
      const rowTotal = (data: any): number => {
        if (!data) return 0;
        // Sum known status numeric fields
        const keys = [
          'active',
          'running',
          'shutdown',
          'criticalFailure',
          'restarted',
          'cycles',
          'offline',
          'commFail',
          'noData',
        ];
        let sum = 0;
        for (const k of keys) {
          const v = data[k];
          if (typeof v === 'number') sum += v;
        }
        return sum;
      };

      let filteredRows = serverRows.filter((r) => rowTotal(r.data) > 0);
      const totalIndex = filteredRows.findIndex((r) => r.label.toLowerCase() === 'total');
      if (totalIndex !== -1) {
        const [totalRow] = filteredRows.splice(totalIndex, 1);
        filteredRows = [totalRow, ...filteredRows];
      }
      return filteredRows;
    }

    // Build normalized/canonical set of selected mop names (handle comma-separated entries)
    const selectedSet = new Set<string>();
    rawSelected.forEach((val) => {
      val
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
        .forEach((v) => {
          const canonical = mapToCanonical(v);
          selectedSet.add(normalize(canonical)); // store normalized canonical
        });
    });

    // Helper to find server row by canonical/normalized displayName or key
    const findServerRowFor = (canonicalNormalized: string) => {
      return serverRows.find((r) => {
        const normDisplay = normalize(mapToCanonical(r.data.displayName || r.label || r.key));
        const normKey = normalize(r.key);
        return normDisplay === canonicalNormalized || normKey === canonicalNormalized;
      });
    };

    // Build result list: for each selected canonical mop, include the server row if exists,
    // otherwise create a zeroed placeholder row so UI shows the MOP with zeros.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resultRows: { key: string; label: string; data: any }[] = [];

    // Keep ordering: iterate selectedSet (insertion order of selections)
    for (const sel of Array.from(selectedSet)) {
      const serverMatch = findServerRowFor(sel);
      if (serverMatch) {
        resultRows.push(serverMatch);
      } else {
        // placeholder key: use normalized canonical as key
        const canonicalName = (() => {
          // find original canonical string from selectedSet by reversing normalization using rawSelected
          // fallback to capitalized version of sel
          const found = rawSelected.map((s) => mapToCanonical(s)).find((c) => normalize(c) === sel);
          return (
            found ||
            sel
              .split(' ')
              .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
              .join(' ')
          );
        })();

        const placeholderKey = sel.replace(/\s+/g, '');

        const zeroedData = {
          mop: canonicalName,
          displayName: canonicalName,
          // zero values for all statuses used by UI
          active: 0,
          running: 0,
          shutdown: 0,
          criticalFailure: 0,
          restarted: 0,
          cycles: 0,
          offline: 0,
          noData: 0,
          commFail: 0,
        };

        // The grid expects row.data to have keys like active,running,etc or mop-specific mopData.
        // To be safe, provide both: a legacy-style mopData array with zero counts, and also numeric fields above.
        const placeholderData = {
          ...zeroedData,
          mopData: [
            { status: 'Active', count: 0 },
            { status: 'Running', count: 0 },
            { status: 'Shutdown', count: 0 },
            { status: 'CommFail', count: 0 },
            { status: 'Restarted', count: 0 },
            { status: 'Offline', count: 0 },
            { status: 'Cycles', count: 0 },
            { status: 'NoData', count: 0 },
          ],
        };

        resultRows.push({
          key: placeholderKey,
          label: canonicalName.toUpperCase(),
          data: placeholderData,
        });
      }
    }

    return resultRows;
  }, [mopTypes, selectedFilters]);

  // Build per-status data
  const statusData = useAppSelector((state) => {
    const result: Record<string, { loading: boolean; values: Record<string, number> }> = {};
    statusColumns.forEach((c) => {
      const key = c.statusKey.toLowerCase();
      result[key] = {
        loading: selectStatusLoading(state, c.statusKey),
        values: selectStatusValues(state, c.statusKey),
      };
    });
    return result;
  });

  // CRITICAL FIX: Only fetch status counts once when raw data loads, not when mopTypes change
  // The previous useEffect created an infinite loop because status reducers update mopTypes
  const [statusCallsDispatched, setStatusCallsDispatched] = useState(false);

  useEffect(() => {
    // Only dispatch once when we have raw data and haven't dispatched before
    if (raw && raw.values && raw.values.length > 0 && !statusCallsDispatched && fetchDataSummary) {
      statusColumns.forEach((col) => {
        dispatch(fetchStatusCounts(col.statusKey));
      });
      setStatusCallsDispatched(true);
    }
  }, [dispatch, raw?.id, statusCallsDispatched]); // Only depend on raw data ID to avoid loops

  useEffect(() => {
    if (raw && raw.values && raw.values.length > 0) {
      dispatch(fetchApplicationsIdMap());
    }
  }, [dispatch, raw?.id]);

  useEffect(() => {
    const checkScrollbar = () => {
      if (bodyContainerRef.current) {
        setHasScrollbar(bodyContainerRef.current.scrollHeight > bodyContainerRef.current.clientHeight);
      }
    };

    const timer = setTimeout(checkScrollbar, 50);
    window.addEventListener('resize', checkScrollbar);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkScrollbar);
    };
  }, [mopTypeRows, loading]);

  if (error) {
    return <div className='wsw-grid-error'>Error: {error}</div>;
  }

  return (
    <div className='wsw-grid-root'>
      {/* Fixed Header */}

      {mopTypeRows && mopTypeRows.length > 0 && statusColumns && statusColumns.length > 0 ? (
        <>
          <div className={`wsw-grid-header-container ${hasScrollbar ? 'scrollbar-present' : ''}`}>
            <div className='wsw-grid-row-flex wsw-grid-header'>
              <div className='wsw-grid-col wsw-grid-mop'></div>
              {statusColumns.map((column) => (
                <div key={column.key} className='wsw-grid-col'>
                  <div
                    className='wsw-grid-col-header'
                    style={{
                      backgroundColor: column.bgColor,
                      border: '1px solid ' + column.borderColor,
                    }}
                  >
                    <span className='wsw-grid-col-header-label' style={{ color: column.fontColor }}>
                      {column.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div ref={bodyContainerRef} className='wsw-grid-body-container'>
            {/* Data rows */}
            {mopTypeRows.map((row) => {
              const isTotal = row.label.trim().toUpperCase() === 'TOTAL';
              return (
                <React.Fragment key={row.key}>
                  <div className='wsw-grid-row-flex' style={{ display: 'flex', width: '100%' }}>
                    {/* MOP Column */}
                    <div className='wsw-grid-col wsw-grid-mop'>
                      <div className='wsw-grid-row-header' style={{ display: 'flex', alignItems: 'center' }}>
                        {!isTotal && applicationsIdMap[row.label] && (
                          <NavigationIcons
                            asset={{
                              industryApplicationId: Number(applicationsIdMap[row.label]) || 0,
                            }}
                            isActive={true}
                          />
                        )}
                        <span className='wsw-grid-row-label' style={isTotal ? { color: '#f7f9f9' } : undefined}>
                          {row.label}
                        </span>
                      </div>
                    </div>
                    {/* Status Columns */}
                    {statusColumns.map((column) => {
                      const key = column.statusKey.toLowerCase();
                      const colLoading = statusData[key]?.loading;
                      const colValues = statusData[key]?.values || {};
                      const mopKey = row.key.toLowerCase();
                      const valueFromStatus = colValues[mopKey];
                      const fallback = ((): number => {
                        const k = column.key as keyof typeof row.data;
                        const val = row.data[k];
                        return typeof val === 'number' ? val : 0;
                      })();
                      const finalValue = valueFromStatus !== undefined ? valueFromStatus : fallback;
                      const isClickable = !colLoading && isMopClickable(row.label);

                      return (
                        <div key={column.key} className='wsw-grid-col-inner'>
                          <div
                            className={`wsw-grid-row${isClickable ? ' wsw-grid-row-clickable' : ''}`}
                            onClick={isClickable ? () => handleCellClick(row.label, finalValue, column.key) : undefined}
                          >
                            {colLoading ? (
                              <span className='wsw-grid-row-loading'>...</span>
                            ) : (
                              <span className='wsw-grid-row-value' style={isTotal ? { color: '#f7f9f9' } : undefined}>
                                {finalValue}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className='wsw-row-divider' style={{ width: '100%' }} />
                </React.Fragment>
              );
            })}
          </div>
        </>
      ) : (
        <div className='wsw-grid-empty-state-container'>
          <EmptyState message='There are currently no records available.' secondaryMessage='Please check applied filters.' />
        </div>
      )}

    </div>
  );
};
